import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Textarea } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, 
  faUpload, 
  faVideo, 
  faFileImage, 
  faFilm, 
  faTimes, 
  faPencilAlt,
  faCog,
  faCheck,
  faWandMagicSparkles
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../context/Auth';
import { isAdminUser } from '../../../config/environment';
import { getStylePresets } from '../../../services/imageGenerationApi';
import LibraryImageModal from '../../LibraryImageModal';
import { LibraryItem } from '../../../types';

interface ThumbnailModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectStage: string | null;
  selectedMethod: 'ai_image' | 'upload' | 'extract' | 'existing_caption' | 'existing' | 'library';
  onMethodChange: (method: 'ai_image' | 'upload' | 'extract' | 'existing_caption' | 'existing' | 'library') => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  isGenerating: boolean;
  onGenerate: (advancedParams?: any) => void; // Updated to support advanced motion parameters
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFrameSelected: () => void;
  originalVideoUrl?: string;
  availableMethods?: ('ai_image' | 'upload' | 'extract' | 'existing_caption' | 'existing' | 'library')[];
  onExistingThumbnail?: () => void;
}

const ThumbnailModal: React.FC<ThumbnailModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectStage,
  selectedMethod,
  onMethodChange,
  description,
  onDescriptionChange,
  isGenerating,
  onGenerate,
  onImageUpload,
  onFrameSelected,
  originalVideoUrl,
  availableMethods = ['ai_image', 'upload', 'extract', 'existing_caption'], // Default fallback
  onExistingThumbnail
}) => {
  const { t } = useTranslation('thumbnailModal');
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Advanced settings state
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<'runway' | 'sdxl' | 'banana_nano'>('runway');
  
  // Week 1: Advanced Image Controls State
  const [selectedStylePreset, setSelectedStylePreset] = useState<string>('');
  const [selectedLightingStyle, setSelectedLightingStyle] = useState<string>('');
  const [qualityPreset, setQualityPreset] = useState<string>('standard');
  const [enhanceDetails, setEnhanceDetails] = useState<boolean>(false);
  const [colorBoost, setColorBoost] = useState<boolean>(false);
  const [improveComposition, setImproveComposition] = useState<boolean>(false);
  const [referenceImages, setReferenceImages] = useState<any[]>([]);
  const referenceImageInputRef = useRef<HTMLInputElement>(null);
  
  // Library modal state
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);

  // Reference image upload handler
  const handleReferenceImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newImages = Array.from(files).slice(0, 3 - referenceImages.length); // Max 3 total
      const imagePromises = newImages.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              uri: e.target?.result,
              tag: `ref_${referenceImages.length + 1}`,
              file: file
            });
          };
          reader.readAsDataURL(file);
        });
      });
      
      Promise.all(imagePromises).then(images => {
        setReferenceImages(prev => [...prev, ...images]);
      });
    }
  };
  
  // Library image selection handler
  const handleLibraryImageSelected = (item: LibraryItem) => {
    console.log('📚 Library image selected:', item);
    setIsLibraryModalOpen(false);
    
    // Navigate to thumbnail editor with the selected image
    navigate(`/thumbnail-editor/${projectId}?baseImage=${encodeURIComponent(item.url)}`);
    
    // Close the main thumbnail modal
    onClose();
  };
  
  // Use existing auth system
  const { user } = useAuth();
  const isAdmin = user ? isAdminUser(user) : false;

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#2d2d2d',
          borderRadius: '12px',
          padding: '0',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '85vh',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ color: 'white', margin: 0, fontSize: '1.5rem' }}>
            {t('thumbnailModal.title')}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Advanced Settings Gear Icon */}
            <button
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
              style={{
                background: showAdvancedSettings ? '#8b5cf6' : 'none',
                border: '1px solid #8b5cf6',
                color: showAdvancedSettings ? 'white' : '#8b5cf6',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              title="Advanced Settings"
            >
              <FontAwesomeIcon icon={faCog} />
            </button>
            {/* Close Button */}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#ccc',
                fontSize: '1.5rem',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '4px'
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div style={{ 
          padding: '1.5rem', 
          overflowY: 'auto', 
          flex: 1,
          maxHeight: 'calc(85vh - 140px)'
        }}>
          {/* Advanced Settings Panel */}
          {showAdvancedSettings && (
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ color: '#8b5cf6', margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600' }}>
                ⚙️ Advanced Settings
              </h4>
              
              {/* Admin Provider Toggle */}
              {isAdmin && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500', color: '#8b5cf6' }}>
                    <input
                      type="checkbox"
                      checked={selectedProvider === 'sdxl'}
                      onChange={(e) => setSelectedProvider(e.target.checked ? 'sdxl' : 'runway')}
                      style={{
                        width: '16px',
                        height: '16px',
                        accentColor: '#8b5cf6'
                      }}
                    />
                    Use SDXL (Admin Only)
                  </label>
                  <div style={{ fontSize: '12px', color: '#999', marginLeft: '24px', marginTop: '4px' }}>
                    Default
                  </div>
                </div>
              )}

              {/* Provider Info */}
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#2a2a2a', 
                borderRadius: '6px',
                border: '1px solid rgba(139, 92, 246, 0.2)'
              }}>
                {/* <div style={{ 
                  color: selectedProvider === 'runway' ? '#10b981' : '#8b5cf6', 
                  fontSize: '14px', 
                  fontWeight: '500' 
                }}>
                  🤖 Current Provider: {selectedProvider === 'runway' ? '🚀 Runway Gen-4' : '⚡ SDXL Turbo'}
                </div> */}
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  {selectedProvider === 'runway' 
                    ? 'High-quality image generation with style transfer support' 
                    : 'Fast batch generation with 4 image variants and quality presets'
                  }
                </div>
              </div>

              {/* Week 1: Advanced Image Controls */}
              {selectedProvider === 'runway' && (
                <div style={{ marginTop: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h4 style={{ color: '#8b5cf6', margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                      🎨 Advanced Image Controls
                    </h4>
                    <div style={{ 
                      fontSize: '11px', 
                      color: selectedStylePreset || selectedLightingStyle ? '#10b981' : '#666',
                      fontWeight: '500'
                    }}>
                      {selectedStylePreset || selectedLightingStyle ? 
                        'Custom settings active' : 
                        'Default settings'
                      }
                    </div>
                  </div>

                  {/* Style Presets */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ color: '#ccc', fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                      Style Presets
                    </label>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                      gap: '6px' 
                    }}>
                      {[
                        { key: 'photorealistic', label: '📸 Photo', desc: 'Realistic style' },
                        { key: 'cinematic', label: '🎬 Cinematic', desc: 'Movie-like' },
                        { key: 'artistic', label: '🎨 Artistic', desc: 'Creative style' },
                        { key: 'commercial', label: '💼 Commercial', desc: 'Professional' },
                        { key: 'portrait', label: '👤 Portrait', desc: 'People focus' }
                      ].map(preset => (
                        <button
                          key={preset.key}
                          onClick={() => setSelectedStylePreset(selectedStylePreset === preset.key ? '' : preset.key)}
                          style={{
                            background: selectedStylePreset === preset.key ? '#8b5cf6' : '#333',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            borderRadius: '6px',
                            padding: '8px',
                            color: 'white',
                            fontSize: '11px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedStylePreset !== preset.key) {
                              e.currentTarget.style.backgroundColor = '#444';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedStylePreset !== preset.key) {
                              e.currentTarget.style.backgroundColor = '#333';
                            }
                          }}
                        >
                          <div style={{ fontWeight: '600', marginBottom: '2px' }}>{preset.label}</div>
                          <div style={{ fontSize: '10px', color: '#aaa' }}>{preset.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Lighting Controls */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ color: '#ccc', fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                      Lighting Style
                    </label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {[
                        { value: 'natural', label: '☀️ Natural', desc: 'Soft daylight' },
                        { value: 'dramatic', label: '🎭 Dramatic', desc: 'High contrast' },
                        { value: 'soft', label: '💡 Soft', desc: 'Even lighting' },
                        { value: 'studio', label: '📷 Studio', desc: 'Professional' }
                      ].map(lighting => (
                        <button
                          key={lighting.value}
                          onClick={() => setSelectedLightingStyle(selectedLightingStyle === lighting.value ? '' : lighting.value)}
                          style={{
                            background: selectedLightingStyle === lighting.value ? '#8b5cf6' : '#333',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            borderRadius: '4px',
                            padding: '6px 10px',
                            color: 'white',
                            fontSize: '11px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s ease',
                            flex: '1',
                            minWidth: '80px'
                          }}
                        >
                          <div style={{ fontWeight: '600', marginBottom: '1px' }}>{lighting.label}</div>
                          <div style={{ fontSize: '9px', color: '#aaa' }}>{lighting.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quality & Enhancement */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ color: '#ccc', fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                      Quality & Enhancement
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {/* Quality Preset */}
                      <div>
                        <div style={{ fontSize: '12px', color: '#8b5cf6', marginBottom: '6px', fontWeight: '500' }}>
                          Quality Level
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {[
                            { value: 'draft', label: 'Draft', desc: 'Fast' },
                            { value: 'standard', label: 'Standard', desc: 'Balanced' },
                            { value: 'high', label: 'High', desc: 'Detailed' },
                            { value: 'ultra', label: 'Ultra', desc: 'Maximum' }
                          ].map(quality => (
                            <button
                              key={quality.value}
                              onClick={() => setQualityPreset(quality.value)}
                              style={{
                                background: qualityPreset === quality.value ? '#8b5cf6' : '#333',
                                border: '1px solid rgba(139, 92, 246, 0.3)',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                color: 'white',
                                fontSize: '10px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <span style={{ fontWeight: '500' }}>{quality.label}</span>
                              <span style={{ color: '#aaa', fontSize: '9px' }}>{quality.desc}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Enhancement Options */}
                      <div>
                        <div style={{ fontSize: '12px', color: '#8b5cf6', marginBottom: '6px', fontWeight: '500' }}>
                          Enhancements
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#ccc', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={enhanceDetails}
                              onChange={(e) => setEnhanceDetails(e.target.checked)}
                              style={{
                                width: '14px',
                                height: '14px',
                                accentColor: '#8b5cf6'
                              }}
                            />
                            Detail Enhancement
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#ccc', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={colorBoost}
                              onChange={(e) => setColorBoost(e.target.checked)}
                              style={{
                                width: '14px',
                                height: '14px',
                                accentColor: '#8b5cf6'
                              }}
                            />
                            Color Boost
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#ccc', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={improveComposition}
                              onChange={(e) => setImproveComposition(e.target.checked)}
                              style={{
                                width: '14px',
                                height: '14px',
                                accentColor: '#8b5cf6'
                              }}
                            />
                            Improve Composition
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reference Images Support */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ color: '#ccc', fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                      Reference Images (Style Transfer)
                    </label>
                    <div style={{
                      border: '2px dashed rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      textAlign: 'center',
                      backgroundColor: 'rgba(139, 92, 246, 0.05)'
                    }}>
                      <div style={{ color: '#8b5cf6', fontSize: '12px', marginBottom: '6px' }}>
                        📎 Upload reference images for style consistency
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleReferenceImageUpload}
                        style={{ display: 'none' }}
                        ref={referenceImageInputRef}
                      />
                      <button
                        onClick={() => referenceImageInputRef.current?.click()}
                        style={{
                          backgroundColor: '#8b5cf6',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          color: 'white',
                          fontSize: '11px',
                          cursor: 'pointer',
                          marginBottom: '6px'
                        }}
                      >
                        Choose Files
                      </button>
                      <div style={{ fontSize: '10px', color: '#999' }}>
                        {referenceImages.length > 0 ? `${referenceImages.length} image(s) selected` : 'Up to 3 reference images'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Existing Thumbnail Notice */}
          {projectStage === 'THUMBNAIL_APP' && (
            <div style={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ color: '#10b981', fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                🖼️
              </div>
              <h4 style={{ color: '#10b981', margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
                Existing Thumbnail Found
              </h4>
              <p style={{ color: '#a0a0a0', margin: 0, fontSize: '0.9rem' }}>
                This project already has a thumbnail. You can continue editing it or generate a new one.
              </p>
            </div>
          )}
          
          {/* Method Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ color: 'white', marginBottom: '1rem', fontSize: '1.2rem' }}>
              {t('thumbnailModal.methodSelection.title')}
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              {/* Existing Thumbnail Option */}
              {availableMethods.includes('existing') && (
                <button
                  onClick={() => onExistingThumbnail?.()}
                  style={{
                    background: selectedMethod === 'existing' ? '#28a745' : '#333',
                    border: selectedMethod === 'existing' ? '1px solid #28a745' : '1px solid #444',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMethod !== 'existing') {
                      e.currentTarget.style.backgroundColor = '#444';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedMethod !== 'existing') {
                      e.currentTarget.style.backgroundColor = '#333';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} style={{ fontSize: '1.2rem', color: selectedMethod === 'existing' ? 'white' : '#28a745' }} />
                  <span>{t('thumbnailModal.methodSelection.existing')}</span>
                </button>
              )}

              {/* AI Image Generation */}
              {availableMethods.includes('ai_image') && (
                <button
                  onClick={() => onMethodChange('ai_image')}
                  style={{
                    background: selectedMethod === 'ai_image' ? '#3a86ff' : '#333',
                    border: selectedMethod === 'ai_image' ? '1px solid #3a86ff' : '1px solid #444',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMethod !== 'ai_image') {
                      e.currentTarget.style.backgroundColor = '#444';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedMethod !== 'ai_image') {
                      e.currentTarget.style.backgroundColor = '#333';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} style={{ fontSize: '1.2rem', color: selectedMethod === 'ai_image' ? 'white' : '#3a86ff' }} />
                  <span>{t('thumbnailModal.methodSelection.aiImage')}</span>
                </button>
              )}

              {/* Upload Option */}
              {availableMethods.includes('upload') && (
                <button
                  onClick={() => onMethodChange('upload')}
                  style={{
                    background: selectedMethod === 'upload' ? '#3a86ff' : '#333',
                    border: selectedMethod === 'upload' ? '1px solid #3a86ff' : '1px solid #444',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMethod !== 'upload') {
                      e.currentTarget.style.backgroundColor = '#444';
                    }
                  }}
                  onMouseLeave={(e) => {
                  if (selectedMethod !== 'upload') {
                    e.currentTarget.style.backgroundColor = '#333';
                  }
                }}
              >
                <FontAwesomeIcon icon={faFileImage} style={{ fontSize: '1.2rem', color: selectedMethod === 'upload' ? 'white' : '#3a86ff' }} />
                <span>{t('thumbnailModal.methodSelection.upload')}</span>
              </button>
              )}

              {/* Load from Library Option */}
              {availableMethods.includes('library') && (
                <button
                  onClick={() => onMethodChange('library')}
                  style={{
                    background: selectedMethod === 'library' ? '#3a86ff' : '#333',
                    border: selectedMethod === 'library' ? '1px solid #3a86ff' : '1px solid #444',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMethod !== 'library') {
                      e.currentTarget.style.backgroundColor = '#444';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedMethod !== 'library') {
                      e.currentTarget.style.backgroundColor = '#333';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faImage} style={{ fontSize: '1.2rem', color: selectedMethod === 'library' ? 'white' : '#3a86ff' }} />
                  <span>{t('thumbnailModal.methodSelection.library')}</span>
                </button>
              )}

              {/* Extract from Video Option */}
              {availableMethods.includes('extract') && (
                <button
                  onClick={() => onMethodChange('extract')}
                  style={{
                    background: selectedMethod === 'extract' ? '#3a86ff' : '#333',
                    border: selectedMethod === 'extract' ? '1px solid #3a86ff' : '1px solid #444',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMethod !== 'extract') {
                      e.currentTarget.style.backgroundColor = '#444';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedMethod !== 'extract') {
                      e.currentTarget.style.backgroundColor = '#333';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faFilm} style={{ fontSize: '1.2rem', color: selectedMethod === 'extract' ? 'white' : '#3a86ff' }} />
                  <span>{t('thumbnailModal.methodSelection.extract')}</span>
                </button>
              )}

              {/* Generate from Captions Option */}
              {availableMethods.includes('existing_caption') && (
                <button
                  onClick={() => onMethodChange('existing_caption')}
                  style={{
                    background: selectedMethod === 'existing_caption' ? '#3a86ff' : '#333',
                    border: selectedMethod === 'existing_caption' ? '1px solid #3a86ff' : '1px solid #444',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedMethod !== 'existing_caption') {
                    e.currentTarget.style.backgroundColor = '#444';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedMethod !== 'existing_caption') {
                    e.currentTarget.style.backgroundColor = '#333';
                  }
                }}
              >
                <FontAwesomeIcon icon={faVideo} style={{ fontSize: '1.2rem', color: selectedMethod === 'existing_caption' ? 'white' : '#3a86ff' }} />
                <span>{t('thumbnailModal.methodSelection.captions')}</span>
              </button>
              )}
            </div>
          </div>

          {/* Method-specific Content */}
          {/* AI Image method goes directly to AI Generator - no input needed */}
          {selectedMethod === 'ai_image' && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>
                Click "{t('thumbnailModal.buttons.generate')}" to create your thumbnail with advanced AI tools
              </p>
            </div>
          )}

          {selectedMethod === 'upload' && (
            <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              <p style={{ color: 'white', marginBottom: '1rem' }}>
                {t('thumbnailModal.hints.uploadImage')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  backgroundColor: '#3a86ff',
                  border: 'none',
                  color: 'white',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a76ef';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3a86ff';
                }}
              >
                <FontAwesomeIcon icon={faUpload} style={{ marginRight: '0.5rem' }} />
                {t('thumbnailModal.hints.uploadImageButton')}
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={onImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </div>
          )}

          {selectedMethod === 'library' && (
            <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              <p style={{ color: 'white', marginBottom: '1rem' }}>
                {t('thumbnailModal.hints.loadFromLibrary')}
              </p>
              <Button
                onClick={() => {
                  setIsLibraryModalOpen(true);
                }}
                style={{
                  backgroundColor: '#3a86ff',
                  border: 'none',
                  color: 'white',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a76ef';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3a86ff';
                }}
              >
                <FontAwesomeIcon icon={faImage} style={{ marginRight: '0.5rem' }} />
                {t('thumbnailModal.hints.browseLibrary')}
              </Button>
            </div>
          )}

          {selectedMethod === 'extract' && (
            <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              <p style={{ color: 'white', marginBottom: '1rem' }}>
                {t('thumbnailModal.hints.extractFrame')}
              </p>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onFrameSelected();
                }}
                style={{
                  backgroundColor: '#3a86ff',
                  border: 'none',
                  color: 'white',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a76ef';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3a86ff';
                }}
              >
                <FontAwesomeIcon icon={faFilm} style={{ marginRight: '0.5rem' }} />
                {t('thumbnailModal.hints.extractFrameButton')}
              </Button>
            </div>
          )}

          {selectedMethod === 'existing_caption' && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: 'white' }}>
                {t('thumbnailModal.hints.generateFromCaptions')}
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{
          display: 'flex',
          justifyContent: projectStage === 'THUMBNAIL_APP' ? 'space-between' : 'flex-end',
          gap: '1rem',
          padding: '1rem 1.5rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          flexShrink: 0
        }}>
          {/* Continue with Existing Thumbnail button */}
          {projectStage === 'THUMBNAIL_APP' && (
            <Button
              onClick={() => {
                onClose();
                navigate(`/thumbnail-editor/${projectId}`);
              }}
              style={{
                backgroundColor: '#3a86ff',
                border: 'none',
                color: 'white',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: 'normal'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2a76ef';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3a86ff';
              }}
            >
              <FontAwesomeIcon icon={faPencilAlt} style={{ marginRight: '0.5rem' }} />
              Continue with Existing
            </Button>
          )}
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button
              onClick={onClose}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #444',
                color: '#aaa',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#333';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#aaa';
              }}
            >
              {t('thumbnailModal.buttons.cancel')}
            </Button>
            {selectedMethod !== 'library' && selectedMethod !== 'upload' && selectedMethod !== 'extract' && (
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                if ((selectedMethod as string) === 'upload') {
                  fileInputRef.current?.click();
                } else if ((selectedMethod as string) === 'library') {
                  setIsLibraryModalOpen(true);
                } else {
                  // Prepare advanced parameters including provider selection
                  const advancedParams = {
                    selectedProvider: selectedProvider, // Include the selected provider
                    ...(selectedProvider === 'runway' && {
                      advanced_image: {
                        style_preset: selectedStylePreset,
                        lighting_style: selectedLightingStyle,
                        quality_preset: qualityPreset,
                        enhance_details: enhanceDetails,
                        color_boost: colorBoost,
                        improve_composition: improveComposition,
                        reference_images: referenceImages.map(img => ({
                          uri: img.uri,
                          tag: img.tag
                        }))
                      }
                    })
                  };
                  
                  console.log('🎨 [THUMBNAIL_MODAL] Calling onGenerate with params:', advancedParams);
                  onGenerate(advancedParams);
                }
              }}
              disabled={
                isGenerating || 
                (selectedMethod as string) === 'library'
              }
              style={{
                backgroundColor: isGenerating || 
                  (selectedMethod as string) === 'library'
                  ? '#1a4388' : '#3a86ff',
                border: 'none',
                color: 'white',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: isGenerating || 
                  (selectedMethod as string) === 'library'
                  ? 'default' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.backgroundColor = '#2a76ef';
                }
              }}
              onMouseLeave={(e) => {
                if (!isGenerating) {
                  e.currentTarget.style.backgroundColor = '#3a86ff';
                }
              }}
            >
              {isGenerating ? 'Opening...' : 
               selectedMethod === 'ai_image' ? t('thumbnailModal.buttons.generate') :
               (selectedMethod as string) === 'upload' ? t('thumbnailModal.buttons.useChooseFile') : 
               (selectedMethod as string) === 'library' ? t('thumbnailModal.buttons.useBorrowLibrary') :
               t('thumbnailModal.buttons.useGenerateNewThumbnail')}
            </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Library Image Selection Modal */}
      <LibraryImageModal
        isOpen={isLibraryModalOpen}
        onClose={() => setIsLibraryModalOpen(false)}
        onImageSelected={handleLibraryImageSelected}
        projectId={projectId}
        title={t('thumbnailModal.libraryModal.title')}
        subtitle={t('thumbnailModal.libraryModal.subtitle')}
        selectButtonText={t('thumbnailModal.libraryModal.selectButton')}
      />
    </div>
  );
};

export default ThumbnailModal;
